import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { categories, mainSiteUrl } from '@/mocks/categories';

export default function Navbar() {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-white/90 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-6 lg:px-10">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <a href="/" className="flex items-center gap-2 shrink-0">
            <span
              className={`font-heading text-xl md:text-2xl font-bold tracking-tight transition-colors ${
                scrolled ? 'text-stone-900' : 'text-white'
              }`}
            >
              YOYBUY
            </span>
            <span
              className={`text-xs font-medium px-2 py-0.5 rounded-full border transition-colors ${
                scrolled
                  ? 'border-stone-300 text-stone-500'
                  : 'border-white/40 text-white/70'
              }`}
            >
              .wtf
            </span>
          </a>

          {/* Center nav */}
          <div className="hidden md:flex items-center gap-8">
            <a
              href="/"
              className={`text-sm font-medium transition-colors hover:opacity-70 ${
                scrolled ? 'text-stone-700' : 'text-white/90'
              }`}
            >
              {t('nav.home')}
            </a>

            {/* Category dropdown */}
            <div className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className={`flex items-center gap-1.5 text-sm font-medium transition-colors hover:opacity-70 ${
                  scrolled ? 'text-stone-700' : 'text-white/90'
                }`}
              >
                {t('nav.categories')}
                <i
                  className={`ri-arrow-down-s-line w-4 h-4 flex items-center justify-center transition-transform ${
                    dropdownOpen ? 'rotate-180' : ''
                  }`}
                />
              </button>

              {dropdownOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-72 bg-white rounded-xl shadow-xl border border-stone-100 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                  <div className="p-2 max-h-[420px] overflow-y-auto">
                    {categories.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-stone-50 transition-colors group"
                        onClick={() => setDropdownOpen(false)}
                      >
                        <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-stone-100 text-stone-600 group-hover:bg-rose-50 group-hover:text-rose-500 transition-colors">
                          <i className={`${cat.icon} text-sm w-4 h-4 flex items-center justify-center`} />
                        </span>
                        <span className="text-sm text-stone-700 font-medium">
                          {cat.name}
                        </span>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <a
              href="/article/chto-takoe-yoybuy-spreadsheet"
              className={`text-sm font-medium transition-colors hover:opacity-70 ${
                scrolled ? 'text-stone-700' : 'text-white/90'
              }`}
            >
              {t('nav.articles')}
            </a>
          </div>

          {/* CTA button */}
          <a
            href={mainSiteUrl}
            target="_blank"
            rel="noopener nofollow"
            className="hidden md:inline-flex items-center gap-2 px-5 py-2.5 bg-stone-900 text-white text-sm font-medium rounded-full hover:bg-stone-800 transition-colors whitespace-nowrap"
          >
            {t('nav.goto')}
            <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
          </a>

          {/* Mobile hamburger */}
          <button
            className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg ${
              scrolled ? 'text-stone-700' : 'text-white'
            }`}
            onClick={() => setDropdownOpen(!dropdownOpen)}
          >
            <i className="ri-menu-line text-xl w-6 h-6 flex items-center justify-center" />
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {dropdownOpen && (
        <div className="md:hidden bg-white border-t border-stone-100 shadow-lg">
          <div className="px-4 py-3 space-y-1">
            <a href="/" className="block px-3 py-2.5 text-sm font-medium text-stone-700 rounded-lg hover:bg-stone-50">
              {t('nav.home')}
            </a>
            <div className="px-3 py-2 text-sm font-medium text-stone-500">
              {t('nav.categories')}
            </div>
            {categories.map((cat) => (
              <a
                key={cat.name}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center gap-3 px-3 py-2 pl-8 text-sm text-stone-600 rounded-lg hover:bg-stone-50"
              >
                <i className={`${cat.icon} w-4 h-4 flex items-center justify-center`} />
                {cat.name}
              </a>
            ))}
            <a
              href="/article/chto-takoe-yoybuy-spreadsheet"
              className="block px-3 py-2.5 text-sm font-medium text-stone-700 rounded-lg hover:bg-stone-50"
            >
              {t('nav.articles')}
            </a>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="block px-3 py-2.5 mt-2 text-sm font-medium text-center text-white bg-stone-900 rounded-full"
            >
              {t('nav.goto')}
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}